import React, { useState, useEffect, useCallback, useContext } from "react";
import DataTable from "react-data-table-component";
import axiosInstance from "../axiosConfig";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { AuthContext } from "../AuthContext";
import Swal from "sweetalert2";
import "../assets/CSS/Dashboard.css";
import "./UserTicketViews.css";
import { PhotoCell, Lightbox } from "../components/PhotoViewer";

function AdminTicketView() {
  const { user,  } = useContext(AuthContext);
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({ fromDate: "", toDate: "", status: "" });
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [lightbox, setLightbox] = useState(null);

  // Update isMobile on resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch tickets assigned to the logged-in supervisor using their EmployeeID.
  const fetchTickets = async () => {
    try {
      
      const response = await axiosInstance.get(`/api/tickets/byRole/Admin`);
      setTickets(response.data);
      setFilteredTickets(response.data);
    } catch (error) {
      console.error("Error fetching tickets for Admin:", error);
    }
  };

  useEffect(() => {
    if (user?.EmployeeID) {
      fetchTickets();
    }
  }, [user]);

  // Apply search and date filters.
  const applyFilters = useCallback(() => {
    const { fromDate, toDate, status } = filters;
    const filtered = tickets.filter((ticket) => {
      const ticketDate = ticket.CreatedDate ? new Date(ticket.CreatedDate) : null;
      const matchesSearch =
        searchText === "" ||
        [ticket.TicketID, ticket.Subject, ticket.TicketDescription, ticket.Worker_Name, ticket.Department]
          .join(" ")
          .toLowerCase()
          .includes(searchText.toLowerCase());
      const matchesDate =
        (!fromDate || (ticketDate && ticketDate >= new Date(fromDate))) &&
        (!toDate || (ticketDate && ticketDate <= new Date(toDate).setHours(23, 59, 59, 999)));
      const matchesStatus = !status || String(ticket.TicketStatus || "").toLowerCase() === status.toLowerCase();
      return matchesSearch && matchesDate && matchesStatus;
    });
    setFilteredTickets(filtered);
  }, [tickets, filters, searchText]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const showAlert = (message, type) => {
    const alertPlaceholder = document.getElementById("alertPlaceholder");
    const alertHTML = `
      <div class="alert alert-${type} alert-dismissible fade show" role="alert">
        <strong>${type === "success" ? "Success!" : "Error!"}</strong> ${message}
        <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
      </div>`;
    alertPlaceholder.innerHTML = alertHTML;
    setTimeout(() => {
      alertPlaceholder.innerHTML = "";
    }, 3000);
  };

  // Function to handle status change.
  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      // Prepare the request body.
      // If the newStatus is "solved", include the Auth and SolvedBy.
      let requestBody = { TicketStatus: newStatus };
  
      if (newStatus.toLowerCase() === "solved") {
        requestBody.Auth = "Admin";
        requestBody.SolvedBy = user?.Name;
      }
  
      await axiosInstance.patch(
        `/api/User-tickets/${ticketId}/status`,
        requestBody
      );
  
      showAlert(`Status updated to "${newStatus}" successfully!`, "success");
      fetchTickets();
    } catch (error) {
      console.error("Error updating status:", error);
      showAlert("Failed to update status. Please try again!", "danger");
    }
  };

  const handleApproveNoWork = async (ticket) => {
    const result = await Swal.fire({
      title: "Approve 'No Work'?",
      text: "This will delete the Red Process and close this ticket.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Approve!"
    });

    if (result.isConfirmed) {
      try {
        await axiosInstance.delete(`/api/approve-no-work/${ticket.TicketID}`, {
          data: { 
            SupervisorName: ticket.Supervisor_Name, 
            Department: ticket.Department 
          }
        });
        Swal.fire("Approved!", "No Work Approved. Records cleared.", "success");
        fetchTickets(); // List refresh karne ke liye
      } catch (error) {
        console.error("Error approving:", error);
        Swal.fire("Error!", "Failed to approve.", "error");
      }
    }
  };

  // Define columns for the data table.
  const columns = [
    {
      name: "Ticket #",
      selector: (row) => row.TicketID,
      cell: (row) => <span className="utv-ticket-id">#{row.TicketID}</span>,
      sortable: true,
      width: "80px"
    },
    {
      name: "Raised by worker",
      selector: (row) => (
        <span
        data-bs-toggle="tooltip"
        data-bs-placement="top"
        title={row.Worker_Name}
        >
          <span className="utv-worker-label">{row.Worker_Name}</span>
        </span>
      ),
      sortable: true,
      width: "150px"
    },
    {
      name: "Department",
      selector: (row) => row.Department,
      sortable: true,
      width: "150px"
    },
    {
      name: "Assigned supervisor",
      selector: (row) => (
        <span 
        data-bs-toggle="tooltip"
        data-bs-placement="top"
        title={row.Supervisor_Name}
        > 
        {row.Supervisor_Name}

        </span>
      ),
      sortable: true
    },
    {
      name: "Worker's issue & details",
      selector: (row) => row.Subject,
      sortable: true,
      grow: 2.5,
      minWidth: "390px",
      cell: (row) => (
        <div className="utv-issue-cell">
          <strong title={row.Subject}>{row.Subject || "No subject"}</strong>
          <span title={row.TicketDescription}>{row.TicketDescription || "No details provided"}</span>
        </div>
      ),
    },
    {
      name: "Photo",
      cell: (row) => <PhotoCell src={row.photo_path} label="Ticket Photo" onView={setLightbox} />,
      width: "100px"
    },
      {
        name: "Action / status",
        cell: (row) => (
          <div className={`utv-status-cell ${String(row.TicketStatus || "open").toLowerCase().replace(/\s+/g, "-")}`}>
            <select
              value={row.TicketStatus}
              onChange={(e) => handleStatusChange(row.TicketID, e.target.value)}
              className="form-select form-select-sm utv-status-select"
            >
              <option value="Open">Open — action needed</option>
              <option value="In Progress">In Progress</option>
              <option value="Solved">Solved</option>
            </select>
          </div>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        width: "225px"
      },
      {
        name: "Raised on",
        selector: (row) => 
          new Date(row.CreatedDate).toLocaleString("en-GB", {
            timeZone: "Asia/Kolkata",
            hour12: false, // Optional: use 24-hour format
          }),
        sortable: true,
        width: "200px" // Slightly wider for date + time
      }
  ];

  const statusCount = (status) => tickets.filter(
    (ticket) => String(ticket.TicketStatus || "").toLowerCase() === status.toLowerCase()
  ).length;
  const hasFilters = Boolean(searchText || filters.fromDate || filters.toDate || filters.status);
  const clearFilters = () => {
    setSearchText("");
    setFilters({ fromDate: "", toDate: "", status: "" });
  };

  return (
    <div className="d-flex dashboard utv-shell">
      <Lightbox src={lightbox} onClose={() => setLightbox(null)} />
      <div className={isSidebarVisible ? "sidebar-container" : "sidebar-hidden"}>
        <Sidebar />
      </div>
      <div className="flex-grow-1">
        <Header toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)} isSidebarVisible={isSidebarVisible} />
        <div id="alertPlaceholder"></div>
        <main className="main-container utv-content">
          <section className="utv-hero">
            <div>
              <span className="utv-eyebrow">WORKER → SUPERVISOR • ADMIN OVERVIEW</span>
              <h1>All Worker Support Requests</h1>
              <p>Workers ne supervisors ke naam par jo problem ya support tickets raise kiye hain, un sabka Admin overview. Assigned supervisor, issue aur current action status yahin dekhein.</p>
            </div>
            <div className="utv-role-card">
              <span>Admin view</span>
              <strong>{user?.Name || "Admin"}</strong>
              <small>All unresolved worker requests</small>
            </div>
          </section>

          <section className="utv-summary">
            <div className="utv-stat total"><i className="bi bi-inbox-fill"></i><div><span>Total requests</span><strong>{tickets.length}</strong></div></div>
            <div className="utv-stat open"><i className="bi bi-exclamation-circle-fill"></i><div><span>Open — action needed</span><strong>{statusCount("Open")}</strong></div></div>
            <div className="utv-stat progress-stat"><i className="bi bi-hourglass-split"></i><div><span>In progress</span><strong>{statusCount("In Progress")}</strong></div></div>
            <div className="utv-stat solved"><i className="bi bi-check-circle-fill"></i><div><span>Solved — confirmation pending</span><strong>{statusCount("Solved")}</strong></div></div>
          </section>

          <section className="utv-card">
            <div className="utv-card-heading">
              <div><h2>All worker-raised requests</h2><p>Ye workers ki complaints/support requests hain; supervisor-to-worker assigned tasks nahi.</p></div>
              <span className="utv-count">{filteredTickets.length} shown</span>
            </div>
            <div className="utv-filters">
              <label className="utv-search"><i className="bi bi-search"></i><input type="search" placeholder="Search worker, supervisor, ticket or issue..." value={searchText} onChange={(e) => setSearchText(e.target.value)} /></label>
              <select aria-label="Filter by status" className="utv-status-filter" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                <option value="">All statuses</option>
                <option value="Open">Open</option>
                <option value="In Progress">In Progress</option>
                <option value="Solved">Solved</option>
              </select>
              <input aria-label="From date" type="date" className="utv-date" value={filters.fromDate} onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })} />
              <input aria-label="To date" type="date" className="utv-date" value={filters.toDate} onChange={(e) => setFilters({ ...filters, toDate: e.target.value })} />
              {hasFilters && <button type="button" className="utv-btn clear" onClick={clearFilters}><i className="bi bi-x-circle"></i>Clear</button>}
              <button type="button" className="utv-btn reload" onClick={fetchTickets}><i className="bi bi-arrow-clockwise"></i>Reload</button>
            </div>
          { !isMobile ? (
            <div className="utv-table-wrap"><DataTable columns={columns} data={filteredTickets} pagination highlightOnHover pointerOnHover conditionalRowStyles={[{ when: (row) => String(row.TicketStatus).toLowerCase() === "solved", style: { backgroundColor: "#f2fbf6", borderLeft: "4px solid #35a36f" } }]} /></div>
          ) : (
            <div className="table-mobile">
              {filteredTickets.map((ticket, idx) => (
                <div key={idx} className="card mb-2">
                  <div className="card-body">
                    <h5 className="card-title">Ticket #{ticket.TicketID}</h5>
                    <p className="card-text"><strong>Worker:</strong> {ticket.Worker_Name}</p>
                    <p className="card-text"><strong>Department:</strong> {ticket.Department}</p>
                    <p className="card-text"><strong>Subject:</strong> {ticket.Subject}</p>
                    <p className="card-text"><strong>Description:</strong> {ticket.TicketDescription}</p>
                    <div className="mb-2">
                      <strong>Photo: </strong> 
                      <div className="mt-1 d-inline-block">
                        <PhotoCell src={ticket.photo_path} label="Ticket Photo" onView={setLightbox} />
                      </div>
                    </div>
                    <p className="card-text"><strong>Created:</strong> {new Date(ticket.CreatedDate).toLocaleDateString("en-GB")}</p>
                    <div className="mb-2">
                      <label className="form-label"><strong>Status:</strong></label>
                      
                      {ticket.TicketStatus === "Pending Faisal Approval" ? (
                        <div>
                           <span className="badge bg-warning text-dark mb-2 d-block">Pending Faisal Approval</span>
                           <button 
                             className="btn btn-sm btn-success w-100" 
                             onClick={() => handleApproveNoWork(ticket)}
                           >
                             Approve 'No Work'
                           </button>
                        </div>
                      ) : (
                        // Normal tickets ke liye purana dropdown
                        <select
                          value={ticket.TicketStatus}
                          onChange={(e) => handleStatusChange(ticket.TicketID, e.target.value)}
                          className="form-select form-select-sm"
                        >
                          <option value="Open">Open</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Solved">Solved</option>
                          <option value="Closed">Closed</option>
                        </select>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          </section>
        </main>
      </div>
    </div>
  );
}

export default AdminTicketView;
